import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useTransform } from 'motion/react';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import Button from '../primitives/Button';
import Label from '../primitives/Label';
import Accent from './Accent';
import { labCore, labOutro, labWorlds } from '../../data/lab';

const LEFT_OF_RAIL = 'lg:pl-[calc(var(--gutter)+10rem)]';

// A chapter's copy is fully visible within ±0.16 of its scroll position, gone by ±0.42,
// drifting up and blurring as it leaves (opacity only under reduced motion)
const useChapterStyle = (progress, k, reduced) => {
  const distance = useTransform(progress, r => Math.abs(r - k));
  const opacity = useTransform(distance, [0, 0.16, 0.42], [1, 1, 0]);
  const y = useTransform(progress, r => (reduced ? 0 : (k - r) * 70));
  const filter = useTransform(distance, d => (reduced ? 'none' : `blur(${Math.max(0, d - 0.16) * 30}px)`));
  const pointerEvents = useTransform(opacity, o => (o > 0.6 ? 'auto' : 'none'));
  return { opacity, y, filter, pointerEvents };
};

const keyboardFocus = el => {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
};

// Keyboard focus inside a faded chapter scrolls the experience to it (a click's focus doesn't)
const Chapter = ({ k, progress, reduced, goTo, className, children }) => {
  const style = useChapterStyle(progress, k, reduced);
  return (
    <motion.div style={style} onFocusCapture={e => keyboardFocus(e.target) && goTo(k)} className={cn('absolute inset-0 flex px-[var(--gutter)]', className)}>
      {children}
    </motion.div>
  );
};

// Size first, then TITLE: tailwind-merge drops a leading-* that precedes a font-size class
const TITLE = 'text-balance font-[440] leading-[1] tracking-[-0.04em] [font-stretch:110%]';

/*
 * The copy over the scene, one layer per chapter, scrubbed by the scroll position:
 * (0) the tagline, (1–4) a world per service with its way in, (5) Dogstudio-style list of
 * worlds in huge light type, where hovering a name previews its formation.
 */
const LabChapters = ({ progress, reduced, hidden, goTo, onOpen, onPreview }) => (
  <motion.div className="absolute inset-0 z-10" animate={{ opacity: hidden ? 0 : 1 }} transition={{ duration: 0.6 }} style={{ pointerEvents: hidden ? 'none' : undefined }}>
    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-background via-background/70 to-transparent md:hidden" />

    <Chapter k={0} progress={progress} reduced={reduced} goTo={goTo} className={cn('flex-col justify-end pb-[clamp(5.5rem,13vh,8.5rem)]', LEFT_OF_RAIL)}>
      {/* Time-sensitive: retire this "New" link once Claudeforce stops being news */}
      <Link
        to={labCore.news.to}
        className="group mb-7 inline-flex w-fit items-center gap-3 rounded-full border border-foreground/15 bg-background/40 py-1 pl-1 pr-4 text-[0.8125rem] backdrop-blur-md transition-colors duration-500 hover:border-foreground/35 hover:bg-foreground/[0.06]"
      >
        <span className="rounded-full bg-[hsl(var(--brand-fill))] px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-[hsl(var(--on-brand-fill))]">New</span>
        {labCore.news.text}
        <ArrowRight size={14} aria-hidden="true" className="transition-transform duration-500 ease-out-expo group-hover:translate-x-1" />
      </Link>
      <Label dot>{labCore.label}</Label>
      <h1 className={cn('mt-6 max-w-[15ch] text-[clamp(2.6rem,6.2vw,6.5rem)]', TITLE, 'leading-[0.95] tracking-[-0.045em]')}>
        <Accent text={labCore.title} />
      </h1>
      <p className="label mt-7 text-foreground/70">{labCore.services}</p>
      <span className="label absolute bottom-[clamp(5.5rem,13vh,8.5rem)] right-[var(--gutter)] hidden items-center gap-2 md:inline-flex">
        {labCore.hint}
        <motion.span animate={reduced ? undefined : { y: [0, 4, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}>
          <ArrowDown size={12} aria-hidden="true" />
        </motion.span>
      </span>
    </Chapter>

    {labWorlds.map(world => {
      const right = world.chapter % 2 === 0;
      return (
        <Chapter
          key={world.slug}
          k={world.chapter}
          progress={progress}
          reduced={reduced}
          goTo={goTo}
          className={cn('items-end pb-[clamp(5.5rem,13vh,8rem)] md:items-center md:pb-0', right ? 'md:justify-end' : cn('md:justify-start', LEFT_OF_RAIL))}
        >
          <div className="w-full max-w-[34rem] md:w-[44%] lg:w-[36%]">
            <Label index={world.index}>{world.label}</Label>
            <h2 className={cn('mt-5 text-[clamp(2.1rem,4.2vw,4.25rem)]', TITLE)}>
              <Accent text={world.title} />
            </h2>
            <p className="mt-5 text-[1rem] leading-relaxed text-muted-foreground md:text-[1.0625rem]">{world.description}</p>
            {world.badge && <p className="label mt-5 inline-flex rounded-full border border-brand/40 px-3 py-1.5 text-brand-ink">{world.badge}</p>}
            <div className="mt-8">
              <Button onClick={() => onOpen(world.slug)} arrow magnetic data-cursor="Enter">
                Enter the {world.name} world
              </Button>
            </div>
          </div>
        </Chapter>
      );
    })}

    <Chapter k={5} progress={progress} reduced={reduced} goTo={goTo} className={cn('flex-col justify-end pb-[clamp(5.5rem,13vh,8rem)] md:justify-center md:pb-0', LEFT_OF_RAIL)}>
      <Label>{labOutro.label}</Label>
      <h2 className="sr-only">{labOutro.title.replace(/\*/g, '')}</h2>
      <ul className="mt-6" onMouseLeave={() => onPreview(null)}>
        {labWorlds.map(world => (
          <li key={world.slug}>
            <button
              type="button"
              onMouseEnter={() => onPreview(world.slug)}
              onFocus={() => onPreview(world.slug)}
              onBlur={() => onPreview(null)}
              onClick={() => onOpen(world.slug)}
              data-cursor="Enter"
              className="group flex items-baseline gap-4 py-1 text-left text-[clamp(2.4rem,5.6vw,6rem)] font-[300] leading-[1.02] tracking-[-0.05em] text-foreground/30 transition-colors duration-500 [font-stretch:112%] hover:text-foreground focus-visible:text-foreground focus-visible:outline-none"
            >
              <span className="label text-brand-ink">({world.index})</span>
              <span className="transition-transform duration-700 ease-out-expo group-hover:translate-x-3 group-focus-visible:translate-x-3">{world.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </Chapter>
  </motion.div>
);

export default LabChapters;
